import { TEMPERATURE_MAX, TEMPERATURE_MIN, TEMPERATURE_STEP } from "~/constants/temperature.constant";

export interface TemperatureBounds {
  min: number;
  max: number;
}

interface RoomsTemperatureBounds {
  comfortTemperatureBounds: TemperatureBounds;
  nightTemperatureBounds: TemperatureBounds;
}

interface RoomTemperatureBounds {
  comfortTemperatureBounds: TemperatureBounds;
  nightTemperatureBounds: TemperatureBounds;
}

interface HouseTemperatureBounds {
  absenceTemperatureBounds: TemperatureBounds;
  preComfortTemperatureBounds: TemperatureBounds;
}

interface RoomWithOptionalComfortTemperature {
  comfortTemperature?: number;
}

const createBounds = ({ min, max }: TemperatureBounds): TemperatureBounds => ({
  min,
  max: Math.max(min, max),
});

export const getNightTemperatureBounds = ({ minTemperature }: { minTemperature: number }): TemperatureBounds => {
  const maxTemperature = TEMPERATURE_MAX;

  return createBounds({
    min: minTemperature,
    max: maxTemperature,
  });
};

export const getComfortTemperatureBounds = ({
  minTemperature,
  preComfortTemperature,
}: {
  minTemperature: number;
  preComfortTemperature: number;
}): TemperatureBounds => {
  const boundedMinTemperature = Math.max(minTemperature, preComfortTemperature + TEMPERATURE_STEP);

  return createBounds({
    min: boundedMinTemperature,
    max: TEMPERATURE_MAX,
  });
};

export const getPreComfortTemperatureBounds = ({
  absenceTemperature,
  minComfortTemperature,
}: {
  absenceTemperature: number;
  minComfortTemperature?: number;
}): TemperatureBounds => {
  const boundedMinTemperature = Math.max(TEMPERATURE_MIN, absenceTemperature + TEMPERATURE_STEP);
  const boundedMaxTemperature = Math.min(
    TEMPERATURE_MAX,
    (minComfortTemperature ?? TEMPERATURE_MAX) - TEMPERATURE_STEP,
  );

  return createBounds({
    min: boundedMinTemperature,
    max: boundedMaxTemperature,
  });
};

const getMinTemperature = (temperatures: number[]): number | undefined =>
  temperatures.reduce<number | undefined>(
    (minTemperature, temperature) =>
      minTemperature === undefined ? temperature : Math.min(minTemperature, temperature),
    undefined,
  );

const getMinRoomComfortTemperature = (rooms: RoomWithOptionalComfortTemperature[]): number | undefined =>
  getMinTemperature(
    rooms
      .map(room => room.comfortTemperature)
      .filter((comfortTemperature): comfortTemperature is number => comfortTemperature !== undefined),
  );

export const getRoomTemperatureBounds = ({
  preComfortTemperature,
}: {
  preComfortTemperature: number;
}): RoomTemperatureBounds => ({
  comfortTemperatureBounds: getComfortTemperatureBounds({
    minTemperature: preComfortTemperature + TEMPERATURE_STEP,
    preComfortTemperature,
  }),
  nightTemperatureBounds: getNightTemperatureBounds({
    minTemperature: preComfortTemperature + TEMPERATURE_STEP,
  }),
});

export const getRoomsTemperatureBounds = ({
  absenceTemperature,
  preComfortTemperature,
}: {
  absenceTemperature: number;
  preComfortTemperature: number;
}): RoomsTemperatureBounds => {
  return {
    comfortTemperatureBounds: createBounds({
      min: preComfortTemperature + TEMPERATURE_STEP,
      max: TEMPERATURE_MAX,
    }),
    nightTemperatureBounds: getNightTemperatureBounds({
      minTemperature: Math.max(absenceTemperature, preComfortTemperature) + TEMPERATURE_STEP,
    }),
  };
};

export const getFrostFreeTemperatureBounds = ({
  absenceTemperature,
}: {
  absenceTemperature: number;
}): TemperatureBounds =>
  createBounds({
    min: TEMPERATURE_MIN,
    max: Math.min(TEMPERATURE_MAX, absenceTemperature - TEMPERATURE_STEP),
  });

export const getAbsenceTemperatureBounds = ({
  frostFreeTemperature,
  preComfortTemperature,
}: {
  frostFreeTemperature: number;
  preComfortTemperature: number;
}): TemperatureBounds => {
  const boundedMinTemperature = Math.max(TEMPERATURE_MIN, frostFreeTemperature + TEMPERATURE_STEP);
  const boundedMaxTemperature = Math.min(TEMPERATURE_MAX, preComfortTemperature - TEMPERATURE_STEP);

  return createBounds({
    min: boundedMinTemperature,
    max: boundedMaxTemperature,
  });
};

export const getHouseTemperatureBounds = ({
  frostFreeTemperature,
  preComfortTemperature,
  absenceTemperature,
  rooms,
}: {
  frostFreeTemperature: number;
  preComfortTemperature: number;
  absenceTemperature: number;
  rooms: RoomWithOptionalComfortTemperature[];
}): HouseTemperatureBounds => {
  const minRoomComfortTemperature = getMinRoomComfortTemperature(rooms);

  return {
    absenceTemperatureBounds: getAbsenceTemperatureBounds({
      frostFreeTemperature,
      preComfortTemperature,
    }),
    preComfortTemperatureBounds: getPreComfortTemperatureBounds({
      absenceTemperature,
      minComfortTemperature: minRoomComfortTemperature,
    }),
  };
};

export const clampTemperature = (value: number, bounds: TemperatureBounds): number =>
  Math.min(Math.max(value, bounds.min), bounds.max);
